// tresci.ts — Treści aplikacji (żółty pasek, skórki) przez serwer Control Center (OWNER 2026-10-02).
//
// Przeglądarka rozmawia WYŁĄCZNIE z `/api/cc/admin/tresci/*`. Klucz maszynowy systemu głównego,
// resolver pierwszeństwa i uprawnienia są po stronie serwera; tu jest tylko kształt danych.

import type { ApiClient } from './client';

const BAZA = '/api/cc/admin/tresci';

export type ZakresPaska = 'GLOBAL' | 'CITY' | 'SCHOOL' | 'USER';
export type StanPozycji = 'DRAFT' | 'SCHEDULED' | 'ACTIVE' | 'ENDED' | 'DISABLED' | 'ERROR';

interface Koperta<T> { wynik: T; correlation_id?: string }

export interface PozycjaPaska {
  id: number;
  message: string;
  scope: ZakresPaska;
  cityIds: number[];
  schoolIds: number[];
  userIds: string[];
  startsAt: string | null;
  endsAt: string | null;
  priority: number;
  status: string;
  effectiveStatus: StanPozycji;
  publishedAt: string | null;
  createdAt: string;
  createdBy: string | null;
  updatedAt: string;
  updatedBy: string | null;
}

export interface NowaPozycjaPaska {
  message: string;
  scope: ZakresPaska;
  cityIds: number[];
  schoolIds: number[];
  userIds: string[];
  startsAt: string | null;
  endsAt: string | null;
  priority: number;
  publish?: boolean;
}

export interface WpisHistorii {
  id: number;
  action: string;
  actor: string | null;
  at: string;
  note?: string | null;
  revisionId?: number;
}

export interface RegulaSlad {
  id: number;
  scope: ZakresPaska;
  priority: number;
  publishedAt: string | null;
  message: string;
  reason: string;
}

export interface PodgladOdbiorcy {
  userId: string | null;
  schoolId: number | null;
  cityId: number | null;
  at: string;
  final: { id: number; message: string; scope: ZakresPaska } | null;
  winner: RegulaSlad | null;
  losers: RegulaSlad[];
  reason: string;
}

export interface PozycjaKatalogu { id: number; name: string }

export interface UczenKatalogu {
  userId: string;
  displayName: string;
  maskedEmail: string | null;
  schoolName: string | null;
  cityName: string | null;
  /** Pełne imię i nazwisko — tylko z wyszukiwarki Administracji (OWNER 17.10, skórki „Konkretne osoby”). */
  fullName?: string | null;
  /** Pełny adres e-mail — j.w. */
  email?: string | null;
}

export interface Skorka {
  id: number;
  themeId: string;
  revision: number;
  name: string;
  status: string;
  effectiveStatus: StanPozycji;
  startsAt: string | null;
  endsAt: string | null;
  packageHash: string | null;
  validationErrors: string[];
  createdBy: string | null;
  updatedAt: string;
  updatedBy: string | null;
  package?: unknown;
  /** Miniatura do biblioteki — adres HTTPS grafiki z paczki (system główny), `null` = brak grafiki pod adresem. */
  thumbnail?: string | null;
  /** Najniższy silnik skórek w aplikacji (1 = każda 4.0.x, ≥2 = 4.0.10+). */
  minEngineVersion?: number;
  createdAt?: string;
  activatedAt?: string | null;
  disabledAt?: string | null;
}

/** Grafika skórki zapisana na serwerze systemu głównego — `url` idzie do `assets[].url` paczki. */
export interface ZasobSkorki {
  url: string;
  path: string;
  sha256: string;
  bytes: number;
  mediaType: string;
}

export interface NowaSkorka {
  name: string;
  package: Record<string, unknown>;
  startsAt?: string | null;
  endsAt?: string | null;
  clientValidation: { valid: boolean; reasons: string[] };
}

export interface StanTresci { zapis_podlaczony: boolean; uprawnienia: string[]; czego_brak?: string[] }

// ── SKÓRKI 2.0 (OWNER 16.10): zastosowanie = skórka z biblioteki + gdzie (zakres, adresaci) + kiedy + priorytet ──

export interface AdresatSkorki { id: string; name: string }

/** Stan zastosowania: zaplanowane / aktywne / zakończone / wyłączone (także wyłączona albo błędna rewizja). */
export type StanZastosowania = 'SCHEDULED' | 'ACTIVE' | 'ENDED' | 'DISABLED';

export interface ZastosowanieSkorki {
  id: number;
  revisionId: number;
  themeId: string;
  name: string | null;
  revision: number;
  thumbnail: string | null;
  minEngineVersion: number;
  scope: ZakresPaska;
  targets: AdresatSkorki[];
  priority: number;
  startsAt: string | null;
  endsAt: string | null;
  effectiveStatus: StanZastosowania;
  /** Szacunek uczniów z aktywnym kontem w zakresie; `null` dla zakończonych i wyłączonych. */
  potentialRecipients: number | null;
  createdBy: string | null;
  publishedAt: string;
  disabledAt: string | null;
  disabledBy: string | null;
}

export interface NoweZastosowanie {
  revisionId: number;
  scope: ZakresPaska;
  cityIds: number[];
  schoolIds: number[];
  userIds: string[];
  startsAt: string | null;
  endsAt: string | null;
  /** Brak daty końca wyłącznie po świadomym potwierdzeniu. */
  noEndConfirmed: boolean;
  priority: number;
}

export interface KolizjaZastosowania {
  assignmentId: number | null;
  revisionId: number;
  name: string;
  scope: ZakresPaska;
  priority: number;
  targets: AdresatSkorki[];
  startsAt: string | null;
  endsAt: string | null;
  legacy: boolean;
  outcome: string;
}

export interface WynikZastosowania {
  zastosowanie?: ZastosowanieSkorki | null;
  konflikt?: boolean;
  blad?: string | null;
  konflikty: KolizjaZastosowania[];
  nakladania: KolizjaZastosowania[];
  /** „Zamień na tę skórkę”: zastosowania wyłączone w tej samej operacji. */
  zastapione?: number[];
  correlationId?: string | null;
}

/** „Zamień na tę skórkę” (OWNER 17.10): nowe zastosowanie + zastosowania potwierdzone do wyłączenia. */
export interface ZamianaZastosowania extends NoweZastosowanie {
  replaceAssignmentIds: number[];
}

export interface SladSkorki {
  assignmentId: number | null;
  revisionId: number;
  themeId: string;
  name: string;
  revision: number;
  scope: ZakresPaska;
  priority: number;
  startsAt: string | null;
  endsAt: string | null;
  legacy: boolean;
  thumbnail: string | null;
  reason: string;
}

export interface OdbiorcaWidoku { userId?: string | null; schoolId?: number | null; cityId?: number | null; at?: string | null }

export interface WidokSkorki {
  userId: string | null;
  schoolId: number | null;
  schoolName: string | null;
  cityId: number | null;
  cityName: string | null;
  at: string;
  winner: SladSkorki | null;
  losers: SladSkorki[];
  reason: string;
  /** DOKŁADNIE paczka, którą dostałaby aplikacja (`null` = zwykły wygląd). */
  package: unknown;
}

export interface WpisHistoriiZastosowania { id: number; action: string; actor: string | null; at: string; snapshot: Record<string, unknown> | null }

async function czytaj<T>(api: ApiClient, sciezka: string, query?: Record<string, string | number | null | undefined>): Promise<T> {
  const k = await api.request<Koperta<T>>('main', `${BAZA}${sciezka}`, { query });
  return k.wynik;
}

/**
 * Zapis treści. Limit czasu 60 s (serwer CC czeka na system główny do 30 s; domyślne 20 s klienta
 * zgłaszałoby „brak odpowiedzi", choć zapis jeszcze trwa) — recenzja R5-15 10.10.
 */
async function zapisz<T>(api: ApiClient, metoda: 'POST', sciezka: string, body: unknown): Promise<T> {
  const k = await api.dzialanieTresci<Koperta<T>>(`${BAZA}${sciezka}`, { method: metoda, body, timeoutMs: 60_000 });
  return k.wynik;
}

/** Okno aktywacji/rollbacku: brak końca idzie z jawnym `bezKonca` (serwer CC odrzuca puste okno bez niego). */
const oknoKonca = (endsAt: string | null) => (endsAt === null ? { endsAt, bezKonca: true } : { endsAt });

export const tresciApi = {
  stan: (api: ApiClient) => api.request<StanTresci>('main', `${BAZA}/stan`),

  // pasek
  paski: (api: ApiClient, filtr: { status?: string; scope?: string; cityId?: number; schoolId?: number } = {}) =>
    czytaj<PozycjaPaska[]>(api, '/paski', filtr),
  utworzPasek: (api: ApiClient, p: NowaPozycjaPaska) => zapisz<PozycjaPaska>(api, 'POST', '/paski', p),
  opublikujPasek: (api: ApiClient, id: number) => zapisz<PozycjaPaska>(api, 'POST', `/paski/${id}/opublikuj`, {}),
  wylaczPasek: (api: ApiClient, id: number) => zapisz<PozycjaPaska>(api, 'POST', `/paski/${id}/wylacz`, {}),
  historiaPaska: (api: ApiClient, id: number) => czytaj<WpisHistorii[]>(api, `/paski/${id}/historia`),
  podgladOdbiorcy: (api: ApiClient, userId: string | null, at?: string) =>
    czytaj<PodgladOdbiorcy>(api, '/podglad-paska', { userId, at }),

  // katalogi
  miasta: (api: ApiClient) => czytaj<PozycjaKatalogu[]>(api, '/katalog/miasta'),
  szkoly: (api: ApiClient, miastoId: number) => czytaj<PozycjaKatalogu[]>(api, `/katalog/miasta/${miastoId}/szkoly`),
  // POST: fraza (imię, nazwisko, e-mail ucznia) w treści — adres trafia do logów i historii przeglądarki.
  uczniowie: (api: ApiClient, q: string) => zapisz<UczenKatalogu[]>(api, 'POST', '/katalog/uczniowie/szukaj', { q }),

  // skórki
  skorki: (api: ApiClient) => czytaj<Skorka[]>(api, '/skorki'),
  podgladSkorki: (api: ApiClient, at?: string) => czytaj<unknown>(api, '/skorki/podglad', { at }),
  historiaSkorki: (api: ApiClient, themeId: string) => czytaj<WpisHistorii[]>(api, `/skorki/historia/${themeId}`),
  /** Rewizja z paczką — podgląd w bibliotece rysuje PRAWDZIWĄ paczkę. */
  skorka: (api: ApiClient, id: number) => czytaj<Skorka>(api, `/skorki/${id}`),
  utworzSkorke: (api: ApiClient, s: NowaSkorka) => zapisz<Skorka>(api, 'POST', '/skorki', s),
  wgrajZasobSkorki: (api: ApiClient, nazwaPliku: string, base64: string) =>
    zapisz<ZasobSkorki>(api, 'POST', '/skorki/zasoby', { nazwaPliku, base64 }),
  zaplanujSkorke: (api: ApiClient, id: number, okno: { startsAt: string | null; endsAt: string | null }) =>
    zapisz<Skorka>(api, 'POST', `/skorki/${id}/zaplanuj`, okno),
  // `endsAt = null` przychodzi tu WYŁĄCZNIE po świadomym „bez daty końcowej" na ekranie (koniecUstalony).
  aktywujSkorke: (api: ApiClient, id: number, endsAt: string | null) =>
    zapisz<Skorka>(api, 'POST', `/skorki/${id}/aktywuj`, oknoKonca(endsAt)),
  wylaczSkorke: (api: ApiClient, id: number) => zapisz<Skorka>(api, 'POST', `/skorki/${id}/wylacz`, {}),
  // Biblioteka (OWNER 11.10): treść rewizji jako NOWA rewizja w oknie — historia poprzedniego użycia zostaje.
  zaplanujPonownieSkorke: (api: ApiClient, id: number, okno: { startsAt: string | null; endsAt: string | null }) =>
    zapisz<Skorka>(api, 'POST', `/skorki/${id}/zaplanuj-ponownie`, okno.endsAt === null ? { ...okno, bezKonca: true } : okno),
  rollbackSkorki: (api: ApiClient, id: number, endsAt: string | null) =>
    zapisz<Skorka>(api, 'POST', `/skorki/${id}/rollback`, oknoKonca(endsAt)),

  // Skórki 2.0 — zastosowania i „Sprawdź widok użytkownika" (resolver systemu głównego, ten sam co runtime).
  zastosowania: (api: ApiClient) => czytaj<ZastosowanieSkorki[]>(api, '/skorki/zastosowania'),
  /** Kontrola przed zapisem: odczyt metodą POST (adresaci w treści) — konflikt wraca jako wynik, nie błąd. */
  kontrolaZastosowania: (api: ApiClient, z: NoweZastosowanie) =>
    zapisz<WynikZastosowania>(api, 'POST', '/skorki/zastosowania/kontrola', z),
  zastosujSkorke: (api: ApiClient, z: NoweZastosowanie) => zapisz<WynikZastosowania>(api, 'POST', '/skorki/zastosowania', z),
  /** Jedna operacja systemu głównego: nowe + wyłączenie kolidujących (409 = lista się zmieniła, nic nie zmieniono). */
  zamienZastosowanie: (api: ApiClient, z: ZamianaZastosowania) =>
    zapisz<WynikZastosowania>(api, 'POST', '/skorki/zastosowania/zamien', z),
  wylaczZastosowanie: (api: ApiClient, id: number) =>
    zapisz<WynikZastosowania>(api, 'POST', `/skorki/zastosowania/${id}/wylacz`, {}),
  historiaZastosowania: (api: ApiClient, id: number) => czytaj<WpisHistoriiZastosowania[]>(api, `/skorki/zastosowania/${id}/historia`),
  /** Odczyt metodą POST: identyfikator konta w treści, nie w adresie. */
  widokSkorki: (api: ApiClient, o: OdbiorcaWidoku) => zapisz<WidokSkorki>(api, 'POST', '/skorki/widok', o),
};

/** Zakres zastosowania po polsku — jedno brzmienie na wszystkich ekranach skórek. */
export const ETYKIETA_ZAKRESU: Readonly<Record<ZakresPaska, string>> = {
  GLOBAL: 'Wszyscy',
  CITY: 'Miasto',
  SCHOOL: 'Szkoła',
  USER: 'Konkretne osoby',
};

/** Opis stanu dla operatora — jedno miejsce dla tabel pasków i skórek. */
export const ETYKIETA_STANU: Readonly<Record<StanPozycji, string>> = {
  DRAFT: 'Szkic',
  SCHEDULED: 'Zaplanowany',
  ACTIVE: 'Aktywny',
  ENDED: 'Zakończony',
  DISABLED: 'Wyłączony',
  ERROR: 'Błąd paczki',
};
